import React, { useEffect, useState } from 'react';
import { FaArrowLeft, FaChevronDown, FaChevronUp, FaFacebook, FaHeart, FaMinus, FaPinterest, FaPlus, FaShoppingCart, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProductCard from '../components/products/ProductCard';
import ProductReviews from '../components/reviews/ProductReviews';
import SEO from '../components/SEO/SEO';
import VariantSelector from '../components/products/VariantSelector';
import { addToCart } from '../store/slices/cartSlice';
import { fetchProductById, fetchProducts } from '../store/slices/productsSlice';
import { clearReviews, fetchReviews } from '../store/slices/reviewsSlice';
import { fetchSEO } from '../store/slices/seoSlice';
import { toggleCart } from '../store/slices/uiSlice';
import { generateBreadcrumbSchema, generateProductSchema } from '../utils/structuredData';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tenantState = useSelector((state) => state.tenant);
  const tenant = tenantState.tenant;
  const { currentProduct, items: relatedProducts, loading, error } = useSelector((state) => state.products);
  const settings = useSelector((state) => state.settings.data);
  
  // Get tenant from localStorage as fallback if Redux state is not ready
  const getTenantFromStorage = () => {
    try {
      const stored = localStorage.getItem('tenant');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Error reading tenant from localStorage:', err);
    }
    return null;
  };
  
  // Use tenant from Redux or fallback to localStorage
  const currentTenant = tenant || getTenantFromStorage();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isLiked, setIsLiked] = useState(false);
  const [sessionLikeCount, setSessionLikeCount] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [displayProduct, setDisplayProduct] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    moreInfo: false,
    shipping: false,
    careGuide: false
  });

  useEffect(() => {
    dispatch(fetchProductById(id));
    dispatch(fetchSEO('product-detail'));
    dispatch(fetchReviews({ productId: id }));
    
    return () => {
      dispatch(clearReviews());
    };
  }, [id, dispatch]);

  // Load session-based like status
  useEffect(() => {
    if (currentProduct?._id) {
      const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
      if (likedProducts[currentProduct._id]) {
        setIsLiked(true);
        setSessionLikeCount(likedProducts[currentProduct._id].count || 0);
      }
    }
  }, [currentProduct?._id]);

  useEffect(() => {
    if (currentProduct?.category?._id) {
      dispatch(fetchProducts({
        category: currentProduct.category._id,
        limit: 4,
      }));
    }
  }, [currentProduct, dispatch]);

  // Update display product when variant changes
  useEffect(() => {
    if (!currentProduct) {
      setDisplayProduct(null);
      return;
    }

    if (selectedVariant) {
      // Use variant-specific data
      setDisplayProduct({
        ...currentProduct,
        price: selectedVariant.price !== undefined ? selectedVariant.price : currentProduct.price,
        stock: selectedVariant.stock !== undefined ? selectedVariant.stock : currentProduct.stock,
        images: selectedVariant.image ? [selectedVariant.image, ...(currentProduct.images || [])] : currentProduct.images,
        variant: selectedVariant
      });
    } else {
      // Use base product data
      setDisplayProduct(currentProduct);
    }
  }, [currentProduct, selectedVariant]);

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    // Reset quantity if variant stock is less
    if (variant && variant.stock !== undefined && quantity > variant.stock) {
      setQuantity(Math.max(1, variant.stock));
    }
  };

  if (loading && !currentProduct) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-96 bg-gray-200 rounded mb-8" />
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!loading && !currentProduct) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 mb-4">
          {error || 'Product not found'}
        </p>
        <p className="text-sm text-gray-500 mb-4">Product ID: {id}</p>
        <button
          onClick={() => navigate('/products')}
          className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark transition"
        >
          Back to Products
        </button>
      </div>
    );
  }
  
  const product = displayProduct || currentProduct;
  const currencySymbol = product.currency === 'USD' ? '$' : product.currency === 'EUR' ? '€' : '₹';
  
  // Calculate display price - use variant price if selected, otherwise use product discounted/regular price
  let displayPrice;
  if (selectedVariant && selectedVariant.price !== undefined) {
    // Use variant discounted price if available, otherwise use variant price
    displayPrice = selectedVariant.discountedPrice !== undefined ? selectedVariant.discountedPrice : selectedVariant.price;
  } else {
    // Use product's discounted price or regular price
    displayPrice = product.discountedPrice || product.price;
  }
  const images = product.images && product.images.length > 0 
    ? product.images 
    : ['https://via.placeholder.com/600'];
  const mainImage = images[selectedImageIndex] || images[0];
  const whatsappMessage = encodeURIComponent(
    `Hi! I'm interested in ${currentProduct.name} (${currencySymbol}${displayPrice.toLocaleString()}). Can you provide more details?`
  );

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const handleLike = () => {
    if (!currentProduct?._id) return;
    
    const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
    if (!likedProducts[currentProduct._id]) {
      likedProducts[currentProduct._id] = { count: 0 };
    }
    
    if (isLiked) {
      likedProducts[currentProduct._id].count = Math.max(0, likedProducts[currentProduct._id].count - 1);
      if (likedProducts[currentProduct._id].count === 0) {
        delete likedProducts[currentProduct._id];
        setIsLiked(false);
      }
    } else {
      likedProducts[currentProduct._id].count += 1;
      setIsLiked(true);
    }
    
    setSessionLikeCount(likedProducts[currentProduct._id]?.count || 0);
    localStorage.setItem('likedProducts', JSON.stringify(likedProducts));
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    if (product.stock === 0) {
      toast.error('This product is out of stock');
      return;
    }

    // Ensure tenant is loaded
    let tenantId = currentTenant?._id || currentTenant?.id;
    
    // If tenant not available, try to load it
    if (!tenantId && !tenantState.loading) {
      try {
        const { loadTenant } = await import('../store/slices/tenantSlice');
        const result = await dispatch(loadTenant());
        if (result.payload?.tenant) {
          tenantId = result.payload.tenant._id || result.payload.tenant.id;
        }
      } catch (error) {
        console.error('Error loading tenant:', error);
      }
    }
    
    // If still no tenant, try localStorage
    if (!tenantId) {
      const storedTenant = getTenantFromStorage();
      tenantId = storedTenant?._id || storedTenant?.id;
    }
    
    if (!tenantId) {
      toast.error('Unable to identify store. Please refresh the page.');
      return;
    }

    const addQuantity = Math.min(quantity, product.stock || quantity);
    
    // Create product object with variant info
    const productToAdd = {
      ...product,
      variant: selectedVariant,
      variantAttributes: selectedVariant?.attributes || []
    };
    
    dispatch(addToCart({ product: productToAdd, quantity: addQuantity, tenantId }));
    
    const variantText = selectedVariant 
      ? ` (${selectedVariant.attributes?.map(a => a.value).join(', ')})` 
      : '';
    toast.success(`${product.name}${variantText} added to cart!`);
    dispatch(toggleCart()); // Open cart sidebar
  };

  const handleBuyNow = async () => {
    // Ensure tenant is loaded
    let tenantToUse = currentTenant;
    let tenantId = currentTenant?._id || currentTenant?.id;
    
    // If tenant not available, try to load it
    if (!tenantId && !tenantState.loading) {
      try {
        const { loadTenant } = await import('../store/slices/tenantSlice');
        const result = await dispatch(loadTenant());
        if (result.payload?.tenant) {
          tenantToUse = result.payload.tenant;
          tenantId = tenantToUse._id || tenantToUse.id;
        }
      } catch (error) {
        console.error('Error loading tenant:', error);
      }
    }
    
    // If still no tenant, try localStorage
    if (!tenantId) {
      const storedTenant = getTenantFromStorage();
      tenantToUse = storedTenant;
      tenantId = storedTenant?._id || storedTenant?.id;
    }
    
    if (!tenantId) {
      toast.error('Unable to identify store. Please refresh the page.');
      return;
    }

    // Check tenant settings and Razorpay configuration before proceeding
    try {
      const { default: axios } = await import('axios');
      const { API_URL } = await import('../config/api');
      
      // Fetch tenant settings to verify Razorpay is configured
      const settingsResponse = await axios.get(`${API_URL}/api/public/settings`, {
        headers: {
          'X-Tenant-Identifier': tenantToUse.subdomain || tenantId
        }
      });

      const razorpayKeyId = settingsResponse.data.data?.paymentApi?.razorpayKeyId;
      if (!razorpayKeyId) {
        toast.error('Payment gateway is not configured. Please contact support.');
        return;
      }

      // Verify Razorpay routes are available
      try {
        await axios.get(`${API_URL}/api/razorpay/create-order`, {
          headers: {
            'X-Tenant-Identifier': tenantToUse.subdomain || tenantId
          }
        }).catch(() => {
          // Route exists but might require auth, which is fine
        });
      } catch (error) {
        // Route check failed, but continue if it's just auth required
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          console.warn('[Buy Now] Razorpay route check:', error.message);
        }
      }

      // Add to cart and navigate to checkout
      handleAddToCart();
      setTimeout(() => {
        navigate('/checkout');
      }, 500);
    } catch (error) {
      console.error('[Buy Now] Error checking tenant settings:', error);
      toast.error('Unable to verify payment configuration. Please try again.');
    }
  };

  const productUrl = window.location.href;
  const productTitle = encodeURIComponent(currentProduct.name);
  const productDescription = encodeURIComponent(currentProduct.description || '');
  const productImage = encodeURIComponent(mainImage);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${productTitle}%20${productUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(productUrl)}&text=${productTitle}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(productUrl)}&media=${productImage}&description=${productTitle}`
  };

  const structuredData = [
    generateProductSchema(currentProduct, settings),
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Products', url: '/products' },
      { name: currentProduct.name, url: `/products/${currentProduct._id}` },
    ]),
  ];

  return (
    <>
      <SEO
        page="product-detail"
        title={currentProduct.name}
        description={currentProduct.description || `Buy ${currentProduct.name} - ${currentProduct.category?.name || 'Product'}. ${currencySymbol}${displayPrice.toLocaleString()}`}
        keywords={`${currentProduct.name}, ${currentProduct.category?.name}, ${currentProduct.subcategory?.name || ''}, art frames, spiritual decor`}
        image={mainImage}
        url={`/products/${currentProduct._id}`}
        type="product"
        structuredData={structuredData}
      />
      <div className="container mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition"
      >
        <FaArrowLeft />
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Images */}
        <div>
          <div className="aspect-square rounded-lg overflow-hidden mb-4 bg-gray-100">
            <img
              src={mainImage}
              alt={currentProduct.name}
              className="w-full h-full object-cover"
            />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {images.slice(0, 4).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`aspect-square object-cover rounded overflow-hidden border-2 transition ${
                    selectedImageIndex === idx 
                      ? 'border-primary ring-2 ring-primary' 
                      : 'border-gray-200 hover:border-primary'
                  }`}
                >
                  <img
                    src={img}
                    alt={`${currentProduct.name} ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <span className="inline-block px-3 py-1 bg-secondary-light text-primary rounded-full text-sm mb-4">
            {currentProduct.category?.name || 'Uncategorized'}
          </span>
          <h1 className="font-heading text-4xl font-bold text-black mb-4">
            {currentProduct.name}
          </h1>
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <span className="text-3xl font-bold text-primary">
              {currencySymbol}{displayPrice.toLocaleString()}
            </span>
            {/* Show original price and discount badge only when no variant is selected */}
            {product.discount > 0 && !selectedVariant && (
              <>
                <span className="text-xl text-gray-500 line-through">
                  {currencySymbol}{product.price.toLocaleString()}
                </span>
                <span className="px-3 py-1 bg-secondary text-white rounded-full text-sm font-semibold">
                  {product.discount}% OFF
                </span>
              </>
            )}
            {/* Show variant discount badge and original price when variant has discount */}
            {selectedVariant && selectedVariant.discount > 0 && selectedVariant.price !== undefined && (
              <>
                <span className="text-xl text-gray-500 line-through">
                  {currencySymbol}{selectedVariant.price.toLocaleString()}
                </span>
                <span className="px-3 py-1 bg-secondary text-white rounded-full text-sm font-semibold">
                  {selectedVariant.discount}% OFF
                </span>
              </>
            )}
            {/* Show base price (strikethrough) when variant is selected, no discount, and variant price differs from base */}
            {selectedVariant && (!selectedVariant.discount || selectedVariant.discount === 0) && selectedVariant.price !== undefined && selectedVariant.price !== currentProduct.price && (
              <span className="text-xl text-gray-500 line-through">
                {currencySymbol}{currentProduct.price.toLocaleString()}
              </span>
            )}
            {/* Like Button */}
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                isLiked
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FaHeart className={isLiked ? 'fill-current' : ''} />
              <span>Like</span>
            </button>
          </div>

          {/* Variant Selector - Show if product has variants */}
          {currentProduct?.hasVariants && currentProduct?.variants && currentProduct.variants.length > 0 && (
            <div className="mb-6">
              <VariantSelector
                product={currentProduct}
                onVariantChange={handleVariantChange}
                selectedVariant={selectedVariant}
                currencySymbol={currencySymbol}
              />
            </div>
          )}

          {/* Expandable Sections */}
          <div className="mb-6">
            {/* Description */}
            <div className="border border-gray-200 rounded-lg mb-3">
              <button
                onClick={() => toggleSection('description')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition"
              >
                <h3 className="font-semibold text-lg text-black">Description</h3>
                {expandedSections.description ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              {expandedSections.description && (
                <div className="px-4 pb-4">
                  <p className="text-gray-700 leading-relaxed">
                    {currentProduct.description || 'No description available.'}
                  </p>
                </div>
              )}
            </div>

            {/* More Info */}
            {currentProduct.moreInfo && (
              <div className="border border-gray-200 rounded-lg mb-3">
                <button
                  onClick={() => toggleSection('moreInfo')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition"
                >
                  <h3 className="font-semibold text-lg text-black">More Info</h3>
                  {expandedSections.moreInfo ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.moreInfo && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {currentProduct.moreInfo}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Shipping Information */}
            {settings?.shipping?.information && (
              <div className="border border-gray-200 rounded-lg mb-3">
                <button
                  onClick={() => toggleSection('shipping')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition"
                >
                  <h3 className="font-semibold text-lg text-black">Shipping Information</h3>
                  {expandedSections.shipping ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.shipping && (
                  <div className="px-4 pb-4 space-y-4">
                    {settings.shipping.information.estimatedDelivery && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Estimated Delivery</h4>
                        <p className="text-gray-700">{settings.shipping.information.estimatedDelivery}</p>
                      </div>
                    )}
                    {settings.shipping.information.processingTime && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Processing Time</h4>
                        <p className="text-gray-700">{settings.shipping.information.processingTime}</p>
                      </div>
                    )}
                    {settings.shipping.information.shippingMethods && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Shipping Methods</h4>
                        <p className="text-gray-700">{settings.shipping.information.shippingMethods}</p>
                      </div>
                    )}
                    {settings.shipping.information.returnPolicy && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Return Policy</h4>
                        <p className="text-gray-700">{settings.shipping.information.returnPolicy}</p>
                      </div>
                    )}
                    {settings.shipping.information.careInstructions && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Care Instructions</h4>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {settings.shipping.information.careInstructions}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Care Guide */}
            {currentProduct.careGuide && (
              <div className="border border-gray-200 rounded-lg mb-3">
                <button
                  onClick={() => toggleSection('careGuide')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition"
                >
                  <h3 className="font-semibold text-lg text-black">Care Guide</h3>
                  {expandedSections.careGuide ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.careGuide && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {currentProduct.careGuide}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Social Sharing */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-lg mb-4 text-black">Share this product</h3>
              <div className="flex flex-wrap gap-3">
                <a
                  href={shareLinks.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </a>
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <FaFacebook />
                  <span>Share</span>
                </a>
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition"
                >
                  <FaTwitter />
                  <span>Tweet</span>
                </a>
                <a
                  href={shareLinks.pinterest}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  <FaPinterest />
                  <span>Pin it</span>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-black">Product Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Category:</span>
                <span className="font-semibold text-gray-900">{currentProduct.category?.name || 'N/A'}</span>
              </div>
              {currentProduct.subcategory && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Subcategory:</span>
                  <span className="font-semibold text-gray-900">{currentProduct.subcategory.name || 'N/A'}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Currency:</span>
                <span className="font-semibold text-gray-900">{currentProduct.currency || 'INR'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Stock:</span>
                <span className={`font-semibold ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {product.stock > 0 ? `${product.stock} available` : 'Out of stock'}
                </span>
              </div>
              {selectedVariant?.sku && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Variant SKU:</span>
                  <span className="font-semibold text-gray-900 font-mono text-sm">{selectedVariant.sku}</span>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span className="text-gray-600">SKU:</span>
                <span className="font-semibold text-gray-900 font-mono text-sm">{currentProduct._id}</span>
              </div>
            </div>
          </div>
          {/* Quantity Selector */}
          {product.stock > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quantity
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border rounded-lg hover:bg-gray-100 transition flex items-center justify-center"
                  disabled={quantity <= 1}
                >
                  <FaMinus className="text-xs" />
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.stock || 999}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setQuantity(Math.min(Math.max(1, val), product.stock || val));
                  }}
                  className="w-20 text-center border rounded-lg py-2 font-semibold"
                />
                <button
                  onClick={() => setQuantity(Math.min(quantity + 1, product.stock || quantity + 1))}
                  className="w-10 h-10 border rounded-lg hover:bg-gray-100 transition flex items-center justify-center"
                  disabled={product.stock && quantity >= product.stock}
                >
                  <FaPlus className="text-xs" />
                </button>
                {product.stock && (
                  <span className="text-sm text-gray-600">
                    {product.stock} available
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {product.stock > 0 ? (
              <>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition flex items-center justify-center gap-2 font-semibold"
                >
                  <FaShoppingCart />
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  className="flex-1 bg-secondary text-white px-6 py-3 rounded-lg hover:bg-primary transition font-semibold"
                >
                  Buy Now
                </button>
              </>
            ) : (
              <div className="flex-1 bg-gray-300 text-gray-600 px-6 py-3 rounded-lg text-center font-semibold cursor-not-allowed">
                Out of Stock
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {settings?.whatsappLink ? (
              <a
                href={`${settings.whatsappLink}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-2"
              >
                <FaWhatsapp />
                Order via WhatsApp
              </a>
            ) : null}
            <Link
              to="/contact"
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition text-center"
            >
              Custom Design
            </Link>
          </div>
        </div>
      </div>

      {/* Product Reviews */}
      <ProductReviews productId={id} />

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading text-3xl font-bold text-black mb-6">Related Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts
              .filter((p) => p._id !== currentProduct._id)
              .slice(0, 4)
              .map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ProductDetailPage;

